import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { API_ENDPOINTS } from "../config";

const PaymentSuccess: React.FC = () => {
  const { state, dispatch } = useApp();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verify payment and refresh user info
    const verifyAndRefreshUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No token found");
          setLoading(false);
          return;
        }

        // Get session_id from URL
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        // Verify payment session if session_id is present
        if (sessionId) {
          console.log("[PaymentSuccess] Verifying payment session:", sessionId);
          const verifyResponse = await fetch(
            `${API_ENDPOINTS.PAYMENT_VERIFY_SESSION}?session_id=${sessionId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            console.log("[PaymentSuccess] Payment verification result:", verifyData);
          } else {
            console.error("[PaymentSuccess] Payment verification failed");
          }
        }

        // Fetch updated user info
        const response = await fetch(API_ENDPOINTS.USERS_INFO, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user info");
        }

        const userInfoData = await response.json();

        // Update user in context
        dispatch({
          type: "SET_USER",
          payload: {
            id: userInfoData.id,
            name: userInfoData.name || "user",
            email: userInfoData.email,
            role: userInfoData.type as "therapist" | "normal",
            has_paid: userInfoData.has_paid || false,
            profile_data: userInfoData.profile_data,
            profile_photo_url: userInfoData.profile_photo_url, // Include profile photo
          },
        });

        setLoading(false);

        // Redirect to main screen after 3 seconds
        setTimeout(() => {
          // Mark that we're returning from payment
          const conversationId = sessionStorage.getItem('conversationId');
          console.log("[PaymentSuccess] Auto redirect - conversationId:", conversationId);
          sessionStorage.setItem('returningFromPayment', 'true');
          console.log("[PaymentSuccess] Set returningFromPayment flag");

          const initialScreen = userInfoData.type === "normal" ? "main" : "main-provider";
          dispatch({ type: "SET_SCREEN", payload: initialScreen });
          // Change URL to root to trigger main screen render
          window.history.pushState({}, "", "/");
          // Force re-render by changing location
          window.dispatchEvent(new PopStateEvent("popstate"));
          // Dispatch custom event to trigger restoration
          window.dispatchEvent(new CustomEvent("restoreConversation"));
          console.log("[PaymentSuccess] Dispatched restoreConversation event");
        }, 3000);
      } catch (error) {
        console.error("Error refreshing user info:", error);
        setLoading(false);
      }
    };

    verifyAndRefreshUser();
  }, [dispatch]);

  return (
    <div className="payment-result-page">
      <div className="payment-result-card">
        <div className="payment-result-icon payment-success-icon">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 6L9 17L4 12"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="payment-result-title">Payment Successful!</h1>

        <p className="payment-result-message">
          {loading
            ? "Activating your full access..."
            : "You now have unlimited access to all therapist profiles!"}
        </p>

        <div className="payment-success-features">
          <h3>What's unlocked:</h3>
          <ul className="payment-success-list">
            <li>✓ View full therapist names</li>
            <li>✓ Access all contact details</li>
            <li>✓ See complete profiles</li>
            <li>✓ Unlimited recommendations</li>
          </ul>
        </div>

        {loading ? (
          <div className="payment-redirect-message">
            Redirecting you back to VocreaAI...
          </div>
        ) : (
          <button
            onClick={() => {
              // Mark that we're returning from payment
              const conversationId = sessionStorage.getItem('conversationId');
              console.log("[PaymentSuccess] Button click - conversationId:", conversationId);
              sessionStorage.setItem('returningFromPayment', 'true');
              console.log("[PaymentSuccess] Set returningFromPayment flag");

              const initialScreen = state.user?.role === "normal" ? "main" : "main-provider";
              dispatch({ type: "SET_SCREEN", payload: initialScreen });
              // Change URL to root to trigger main screen render
              window.history.pushState({}, "", "/");
              // Force re-render by changing location
              window.dispatchEvent(new PopStateEvent("popstate"));
              // Dispatch custom event to trigger restoration
              window.dispatchEvent(new CustomEvent("restoreConversation"));
              console.log("[PaymentSuccess] Button click - Dispatched restoreConversation event");
            }}
            className="payment-result-button payment-result-button-primary"
          >
            Continue to VocreaAI
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
